import { ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModuleManagerMetricInfo } from 'src/shared/models/module-manager/module-manager-settings.models';
import { WidgetThresholdsModalService } from './widget-thresholds-modal.service';
import { Subscription } from 'rxjs';
import { ThresholdItem } from './widget-thresholds-modal.model';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';

@Component({
    selector: 'cv-widget-thresholds-modal',
    templateUrl: './widget-thresholds-modal.component.html',
    styleUrls: ['./widget-thresholds-modal.component.scss']
})
export class WidgetThresholdsModalComponent implements OnInit, OnDestroy {

    @Input() moduleKey = '';
    @Input() metrics: ModuleManagerMetricInfo[] = [];

    selectedMetrics: number[] = [];
    previouslySelectedMetrics: number[] = [];
    metricNames = new Map<string, string>();
    metricThresholds: Record<string, ThresholdItem[]> = {};
    isLoading = false;
    selectorOpen = false;
    public formInvalid = false;
    public editMessage = "";

    savePromise: Promise<any> = null;
    subscriptions: Subscription[] = [];
    _openPromise: Promise<void> = null;
    _resolveOpenPromise: () => void = null;

    constructor(
        private activeModal: NgbActiveModal,
        private service: WidgetThresholdsModalService,
        private cvLocalizationService: CVLocalizationService,
        public changeDetector: ChangeDetectorRef,
    ) { }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;
        await this.cvLocalizationService.GetSection("CXOne_Modal");
        this.editMessage = this.cvLocalizationService.getCachedText(
            "CXOne_Modal", "Thresholds_Edit_Message", "Cannot edit. Permissions required."
        );
        this.metricThresholds = await this.service.loadWidgetThresholds(this.moduleKey) ?? {};
        this.selectedMetrics = Object.keys(this.metricThresholds).map(v => parseInt(v, 10));
        this.selectedMetrics = this.selectedMetrics.filter(selected => this.metrics.find(metric => metric.Id === selected));
        this.metrics.forEach(metric => this.metricNames.set(metric.Id.toString(), metric.Name));
        this.sortSelectedMetrics();
        this.previouslySelectedMetrics = this.selectedMetrics;
        this.applyThresholdFormatting();
        this.isLoading = false;
    }

    ngOnDestroy() {
        this._resolveOpenPromise?.();
        this._resolveOpenPromise = null;
        this._openPromise = null;
    }

    cancel() {
        this.activeModal.dismiss();
    }

    save() {
        if (this._openPromise !== null) {
            this._openPromise.then(() => {
                this.prepareForSave();
                this.service.saveWidgetThresholds(this.moduleKey, this.metricThresholds);
                this.activeModal.close();
            });
        } else {
            this.prepareForSave();
            this.service.saveWidgetThresholds(this.moduleKey, this.metricThresholds);
            this.activeModal.close();
        }
    }

    prepareForSave() {
        for (const metricId of this.selectedMetrics) {
            this.metricThresholds[metricId].forEach(threshold => {
                threshold.left = this.prepareValueForSave(threshold.left);
                threshold.right = this.prepareValueForSave(threshold.right);
            });
        }
    }

    prepareValueForSave(value: string | number): number {
        if (typeof value === 'number')
            return value;

        if (value.indexOf(":") > -1) {
            // Parse times out into a single double value
            const timeParts = value.split(":");
            if (timeParts.length === 1)
                return parseInt(value, 10);
            else if (timeParts.length === 2) {
                const minutes = parseInt(timeParts[0], 10);
                const seconds = parseInt(timeParts[1], 10);
                return seconds + (minutes * 60);
            }
            else if (timeParts.length === 3) {
                const hours = parseInt(timeParts[0], 10);
                const minutes = parseInt(timeParts[1], 10);
                const seconds = parseInt(timeParts[2], 10);
                return seconds + (minutes * 60) + (hours * 3600);
            }
            return 0;
        }
        else return parseFloat(value);
    }

    applyThresholdFormatting() {
        for (const metricId of this.selectedMetrics) {
            if (this.getConversionType(metricId) === 4) { // Time type: Time
                this.metricThresholds[metricId].forEach(threshold => {
                    threshold.left = this.formatTimeTypeValue(threshold.left as number);
                    threshold.right = this.formatTimeTypeValue(threshold.right as number);
                });
            }
        }
    }

    formatTimeTypeValue(value: number): string {
        let time = value;
        time = (time / 3600);
        const hours = Math.floor(time);
        time = ((time - hours) * (60));
        const minutes = Math.floor(time);
        time = ((time - minutes) * (60));
        const seconds = Math.round(time);

        const padZero = (digit) => digit.toString().padStart(2, '0');

        return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
    }

    getConversionType(metricId: number) {
        const index = this.metrics.findIndex(m => m.Id === metricId);
        return this.metrics[index].ConversionType;
    }

    getDirection(metricId: number) {
        return this.metrics.find(m => m.Id === metricId).Direction;
    }

    deselectMetric(key: string) {
        const index = this.selectedMetrics.findIndex(m => m === parseInt(key, 10));
        this.selectedMetrics.splice(index, 1);
        this.selectedMetrics = this.selectedMetrics.slice();
        delete this.metricThresholds[key];
        this.previouslySelectedMetrics = this.selectedMetrics;
    }

    onSelectionChanged(event) {
        this.selectedMetrics = event;
        this.sortSelectedMetrics();
        this.updateThresholdsList();
    }

    onSelectorOpened() {
        this._openPromise = new Promise(resolve => this._resolveOpenPromise = resolve);
    }

    onSelectorClosed() {
        this._resolveOpenPromise?.();
        this._resolveOpenPromise = null;
        this._openPromise = null;
    }

    sortSelectedMetrics() {
        this.selectedMetrics.sort((a, b) => {
            const aName = this.metricNames.get(a.toString());
            const bName = this.metricNames.get(b.toString());
            if (aName > bName) return 1;
            else if (aName < bName) return -1;
            else return 0;
        });
    }

    updateThresholdsList() {
        const metricsToAdd = this.selectedMetrics.filter(m => this.previouslySelectedMetrics.indexOf(m) === -1);
        const metricsToDelete = this.previouslySelectedMetrics.filter(m => this.selectedMetrics.indexOf(m) === -1);
        metricsToDelete.forEach(metric =>  {
            delete this.metricThresholds[metric];
        });
        metricsToAdd.forEach(metric => {
            this.metricThresholds[metric] = this.populateNewThresholds(metric);
        });
        this.previouslySelectedMetrics = this.selectedMetrics;
    }

    populateNewThresholds(metricId: number): ThresholdItem[] {
        const type = this.metrics.find(m => m.Id === metricId).ConversionType;
        const thresholds = [
            {enabled: false, left: '0', right: '0'},
            {enabled: false,  left: '0', right: '0'},
            {enabled: false, left: '0', right: '0'}
        ];

        for(let i = 0; i < 3; i++) {
            switch(type) {
                case 0: // Number
                case 1: // Time type: Seconds
                case 2: // Time type: Minutes
                case 3: // Time type: Hours
                {
                    thresholds[i].left = '0';
                    thresholds[i].right = '0';
                    break;
                }
                case 4: // Time type: Time
                {
                    thresholds[i].left = '00:00:00';
                    thresholds[i].right = '00:00:00';
                    break;
                }
                case 5: // percent
                {
                    thresholds[i].left = '0.00';
                    thresholds[i].right = '0.00';
                    break;
                }
            }
        }
        return thresholds;
    }

    cleanTextInput($event, metric, thresholdIndex, level) {
        let cleanedValue = '';
        const type = this.metrics.find(m => m.Id === metric).ConversionType;
        switch(type) {
            case 0: // number
            case 5: // percent
            case 1: // time type: Seconds
            case 2: // time type: Minutes
            case 3: // time type: Hours
            {
                // remove multiple periods and limit to 2 decimal places
                cleanedValue = $event.replace(/[^0-9.]/g, '');
                const segments = cleanedValue.split('.');
                if (segments.length > 2) {
                    cleanedValue = segments[0] + '.' + segments[1] + segments[2];
                }
                const periodIndex = cleanedValue.indexOf('.');
                if (periodIndex > -1) {
                    cleanedValue = cleanedValue.slice(0, periodIndex + 3);
                }
                break;
            }
            case 4: // Time type: Time
            {
                cleanedValue = $event.replace(/[^0-9:]/g, '');
                const segments = cleanedValue.split(':');
                if (segments.length > 3) {
                    cleanedValue = segments[0] + ':' + segments[1] + ':' + segments[2];
                }
                break;
            }
        }

        if (level) {
            this.metricThresholds[metric][thresholdIndex].right = null;
            this.changeDetector.detectChanges();
            this.metricThresholds[metric][thresholdIndex].right = cleanedValue;
        }
        else {
            this.metricThresholds[metric][thresholdIndex].left = null;
            this.changeDetector.detectChanges();
            this.metricThresholds[metric][thresholdIndex].left = cleanedValue;
        }
    }
}
